enum KeyCodes {
    TAB = 9,
    ENTER = 13,
    ESCAPE = 27,
    SPACE = 32,
    LEFT_ARROW = 37,
    UP_ARROW = 38,
    RIGHT_ARROW = 39,
    DOWN_ARROW = 40,
}

const FOCUSABLE_ELEMENTS = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
    'select:not([disabled]):not([aria-hidden])',
    'textarea:not([disabled]):not([aria-hidden])',
    'button:not([disabled]):not([aria-hidden])',
    'iframe',
    'object',
    'embed',
    '[contenteditable]',
    '[tabindex]:not([tabindex^="-"])',
].join(', ');

export type ModalOptions = {
    disableFocus?: Boolean;
    disableScroll?: Boolean;
    awaitOpenAnimation?: Boolean;
    awaitCloseAnimation?: Boolean;
    openClasses?: string[];
    onShow?: (modal: HTMLElement, trigger: HTMLElement, event: Event) => void;
    onClose?: (modal: HTMLElement, trigger: HTMLElement, event: Event) => void;
};

export type ModalInitOptions = {
    modal: HTMLElement;
    openTriggers: HTMLElement[] | string;
    closeTriggers: HTMLElement[] | string;
} & ModalOptions;

const defaultOptions: ModalOptions = {
    disableFocus: false,
    disableScroll: false,
    awaitOpenAnimation: false,
    awaitCloseAnimation: false,
    openClasses: ['is-open'],
};

const defaultInitOptions: ModalInitOptions = {
    ...defaultOptions,
    modal: null,
    openTriggers: 'data-modal-open',
    closeTriggers: 'data-modal-close',
};

export class Modal {
    private static modals: Modal[] = [];

    private isOpen: boolean = false;
    private lastFocusedElement: HTMLElement;

    private constructor(
        private modal: HTMLElement,
        private openTriggers: HTMLElement[],
        private closeTriggers: HTMLElement[],
        private options: ModalOptions = defaultOptions
    ) {
        this.onKeydown = this.onKeydown.bind(this);
        this.onClick = this.onClick.bind(this);
        this.openTriggers = [...this.openTriggers];
        this.closeTriggers = [...this.closeTriggers];

        this.checkIntegrity();
        this.registerEvents();

        Modal.modals = [...Modal.modals, this];
    }

    /**
     * Creates a Modal instance
     *
     * @static
     * @param {ModalInitOptions}
     * @returns {Modal}
     * @memberof Modal
     */
    public static init(initOptions: ModalInitOptions): Modal {
        initOptions = { ...defaultInitOptions, ...initOptions };

        const { modal, openTriggers, closeTriggers, ...options } = initOptions;

        const openers =
            typeof openTriggers == 'string'
                ? Array.from(
                    document.querySelectorAll<HTMLElement>(openTriggers)
                )
                : openTriggers;

        const closers =
            typeof closeTriggers == 'string'
                ? Array.from(
                    document.querySelectorAll<HTMLElement>(closeTriggers)
                )
                : closeTriggers;

        return new Modal(modal, openers, closers, options);
    }

    /**
     * Checks if any Modals are open. If no Modals are given, all existing Modals will be checked.
     *
     * @static
     * @param {...Modal[]} modals
     * @memberof Modal
     */
    public static isOpen(...modals: Modal[]) {
        if (modals.length) return modals.some((modal) => modal.isOpen);

        return !!Modal.getOpen();
    }

    /**
     * Get the currently open Modal
     *
     * @static
     * @returns
     * @memberof Modal
     */
    public static getOpen() {
        return Modal.modals.find((modal) => modal.isOpen);
    }

    /**
     * Shows the modal
     *
     * @memberof Modal
     */
    public show() {
        this.showModal();
    }

    /**
     * Hides the modal
     *
     * @memberof Modal
     */
    public hide() {
        this.closeModal();
    }

    /**
     * Loops through all triggers and binds click event
     * @return {void}
     */
    private registerEvents() {
        this.openTriggers.filter(Boolean).forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                if (this.isOpen) return;

                event.preventDefault();
                this.showModal(event);
            });
        });

        this.closeTriggers.filter(Boolean).forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                if (!this.isOpen) return;

                event.preventDefault();
                event.stopImmediatePropagation();
                this.closeModal(event);
            });
        });
    }

    private showModal(event: Event = null) {
        if (this.isOpen) return;
        this.isOpen = true;

        this.lastFocusedElement = document.activeElement as HTMLElement;
        this.modal.setAttribute('aria-hidden', 'false');
        this.options.openClasses.forEach((openClass) =>
            this.modal.classList.add(openClass)
        );
        this.scrollBehaviour('disable');
        this.addEventListeners();

        if (this.options.awaitOpenAnimation) {
            const handler = () => {
                this.modal.removeEventListener('animationend', handler, false);
                this.setFocusToFirstNode();
            };
            this.modal.addEventListener('animationend', handler, false);
        } else {
            this.setFocusToFirstNode();
        }

        const trigger = event?.currentTarget as HTMLElement;

        if (this.options.onShow)
            this.options.onShow(this.modal, trigger, event);
    }

    private addEventListeners() {
        this.modal.addEventListener('touchstart', this.onClick, { passive: true });
        this.modal.addEventListener('click', this.onClick, { passive: true });
        document.addEventListener('keydown', this.onKeydown, { passive: true });
    }

    private removeEventListeners() {
        this.modal.removeEventListener('touchstart', this.onClick);
        this.modal.removeEventListener('click', this.onClick);
        document.removeEventListener('keydown', this.onKeydown);
    }

    private onClick(event) {
        if (this.closeTriggers.indexOf(event.target) > -1) {
            this.closeModal(event);
        }
    }

    private closeModal(event: Event = null) {
        if (!this.isOpen) return;
        this.isOpen = false;

        this.modal.setAttribute('aria-hidden', 'true');
        this.removeEventListeners();
        this.scrollBehaviour('enable');
        if (this.lastFocusedElement && this.lastFocusedElement.focus) {
            this.lastFocusedElement.focus();
        }

        if (this.options.awaitCloseAnimation) {
            const handler = () => {
                // Check if we're no longer closed
                if (this.modal.getAttribute('aria-hidden') === 'false') return;

                this.options.openClasses.forEach((openClass) =>
                    this.modal.classList.remove(openClass)
                );
                this.modal.removeEventListener('animationend', handler, false);
            };

            this.modal.addEventListener('animationend', handler, false);
        } else {
            this.options.openClasses.forEach((openClass) =>
                this.modal.classList.remove(openClass)
            );
        }

        const trigger = event?.currentTarget as HTMLElement;
        if (this.options.onClose)
            this.options.onClose(this.modal, trigger, event);
    }

    private onKeydown(event) {
        const { keyCode } = event;

        if (keyCode == KeyCodes.ESCAPE) this.closeModal(event);
        else if (keyCode == KeyCodes.TAB) this.retainFocus(event);
    }

    private retainFocus(event) {
        let focusableNodes = this.getFocusableNodes();

        // no focusable nodes
        if (focusableNodes.length === 0) return;

        /**
         * Filters nodes which are hidden to prevent
         * focus leak outside modal
         */
        focusableNodes = focusableNodes.filter((node) => {
            return node.offsetParent !== null;
        });

        // if disableFocus is true
        if (!this.modal.contains(document.activeElement)) {
            focusableNodes[0].focus();
        } else {
            const focusedItemIndex = focusableNodes.indexOf(
                document.activeElement as HTMLElement
            );

            if (event.shiftKey && focusedItemIndex === 0) {
                focusableNodes[focusableNodes.length - 1].focus();
                event.preventDefault();
            }

            if (
                !event.shiftKey &&
                focusableNodes.length > 0 &&
                focusedItemIndex === focusableNodes.length - 1
            ) {
                focusableNodes[0].focus();
                event.preventDefault();
            }
        }
    }

    private getFocusableNodes() {
        return Array.from(
            this.modal.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS)
        );
    }

    /**
     * Tries to set focus on a node which is not a close trigger
     * if no other nodes exist then focuses on first close trigger
     */
    private setFocusToFirstNode() {
        if (this.options.disableFocus) return;

        const focusableNodes = this.getFocusableNodes();

        // No focusable nodes
        if (focusableNodes.length === 0) return;

        // Remove nodes on whose click, the modal closes
        // could not think of a better name :(
        const nodesWhichAreNotCloseTargets = focusableNodes.filter(
            (node) => this.closeTriggers.indexOf(node) === -1
        );

        if (nodesWhichAreNotCloseTargets.length > 0)
            nodesWhichAreNotCloseTargets[0].focus();
        if (nodesWhichAreNotCloseTargets.length === 0)
            focusableNodes[0].focus();
    }

    private scrollBehaviour(toggle: 'enable' | 'disable') {
        if (!this.options.disableScroll) return;

        if (toggle === 'enable')
            Object.assign(document.body.style, { overflow: '' });
        else if (toggle === 'disable')
            Object.assign(document.body.style, { overflow: 'hidden' });
    }

    private checkIntegrity() {
        if (!this.modal) {
            throw new Error('Invalid parameter: modal');
        }

        // if (!this.openTriggers.length) {
        //     throw new Error('Invalid parameter: openTriggers');
        // }

        // if (!this.closeTriggers.length) {
        //     throw new Error('Invalid parameter: closeTriggers');
        // }
    }
}
